import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

interface LockedSectionProps {
  /** Заблокирована ли секция */
  isLocked: boolean;
  /** Содержимое секции */
  children: React.ReactNode;
  /** Заголовок заблокированной секции */
  title?: string;
  /** Описание условия разблокировки */
  unlockCondition?: string;
  /** Номер шага для отображения */
  stepNumber?: number;
  /** Callback при попытке взаимодействия с заблокированной секцией */
  onLockedClick?: () => void;
  /** Показывать ли анимацию разблокировки */
  animateUnlock?: boolean;
  /** Дополнительные CSS классы */
  className?: string;
}

/**
 * Компонент для блокировки контента до выполнения определённых условий
 * Используется для последовательного прохождения шагов урока
 */
export function LockedSection({
  isLocked,
  children,
  title = 'Этот шаг заблокирован',
  unlockCondition,
  stepNumber,
  onLockedClick,
  animateUnlock = true,
  className,
}: LockedSectionProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [wasLocked, setWasLocked] = useState(isLocked);
  const [showCheckmark, setShowCheckmark] = useState(false);

  // Анимация разблокировки
  useEffect(() => {
    if (wasLocked && !isLocked && animateUnlock) {
      setIsAnimating(true);
      setShowCheckmark(true);
      const timer = setTimeout(() => {
        setIsAnimating(false);
        // Скрываем галочку после анимации
        setTimeout(() => setShowCheckmark(false), 500);
      }, 500);
      return () => clearTimeout(timer);
    }
    setWasLocked(isLocked);
  }, [isLocked, wasLocked, animateUnlock]);

  const handleClick = () => {
    if (isLocked && onLockedClick) {
      onLockedClick();
    }
  };

  if (isLocked) {
    return (
      <div
        onClick={handleClick}
        className={cn(
          'relative rounded-lg border border-slate-700/50 bg-slate-900/30 overflow-hidden',
          'cursor-not-allowed select-none',
          'transition-[border-color,background-color] duration-300',
          className
        )}
      >
        {/* Blur overlay */}
        <div className="absolute inset-0 backdrop-blur-sm bg-slate-900/60 z-10" />

        {/* Lock icon and message */}
        <div className="relative z-20 flex flex-col items-center justify-center py-8 px-4">
          {/* Step number badge */}
          {stepNumber !== undefined && (
            <div className="absolute top-4 left-4 flex items-center justify-center w-8 h-8 rounded-full bg-slate-800 border border-slate-700">
              <span className="text-sm font-medium text-slate-400">{stepNumber}</span>
            </div>
          )}

          {/* Lock icon */}
          <div className="relative mb-4">
            <div className="w-16 h-16 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center">
              <svg
                className="w-8 h-8 text-slate-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
            {/* Pulse animation */}
            <div className="absolute inset-0 rounded-full border border-slate-600 animate-ping opacity-20" />
          </div>

          {/* Title */}
          <h3 className="text-lg font-medium text-slate-300 mb-2">{title}</h3>

          {/* Unlock condition */}
          {unlockCondition && (
            <p className="text-sm text-slate-500 text-center max-w-sm">{unlockCondition}</p>
          )}
        </div>

        {/* Blurred content preview */}
        <div className="absolute inset-0 opacity-30 pointer-events-none blur-sm">{children}</div>
      </div>
    );
  }

  // Разблокированное состояние с анимацией
  return (
    <div
      className={cn(
        'relative rounded-lg',
        'transition-[border-color,background-color,box-shadow] duration-500',
        isAnimating && 'animate-unlock animate-completion-glow',
        className
      )}
    >
      {/* Success indicator с улучшенной анимацией */}
      {showCheckmark && (
        <div className="absolute -top-2 -right-2 z-20 w-8 h-8 rounded-full bg-green-500 flex items-center justify-center animate-checkmark-bounce shadow-lg">
          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      )}

      {/* Step number badge */}
      {stepNumber !== undefined && (
        <div
          className={cn(
            'absolute -top-3 -left-3 z-10 flex items-center justify-center w-8 h-8 rounded-full shadow-lg',
            'transition-[background-color,transform] duration-300',
            isAnimating ? 'bg-green-500 animate-checkmark-bounce' : 'bg-git-orange'
          )}
        >
          {isAnimating ? (
            <svg
              className="w-4 h-4 text-white animate-checkmark-appear"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          ) : (
            <span className="text-sm font-bold text-white">{stepNumber}</span>
          )}
        </div>
      )}

      {children}
    </div>
  );
}

/**
 * Компонент для отображения заблокированного шага в списке
 */
interface LockedStepItemProps {
  stepNumber: number;
  title: string;
  isLocked: boolean;
  isCompleted: boolean;
  isCurrent: boolean;
  onClick?: () => void;
}

export function LockedStepItem({
  stepNumber,
  title,
  isLocked,
  isCompleted,
  isCurrent,
  onClick,
}: LockedStepItemProps) {
  const getStatusStyles = () => {
    if (isCompleted) {
      return 'bg-green-500/10 border-green-500/30 text-green-400';
    }
    if (isCurrent) {
      return 'bg-git-orange/10 border-git-orange/30 text-git-orange';
    }
    if (isLocked) {
      return 'bg-slate-800/50 border-slate-700/50 text-slate-500';
    }
    return 'bg-slate-800/50 border-slate-700 text-slate-300';
  };

  return (
    <button
      onClick={onClick}
      disabled={isLocked}
      className={cn(
        'w-full flex items-center gap-3 px-3 py-2 rounded-lg border transition-all',
        getStatusStyles(),
        !isLocked && 'hover:bg-slate-700/50 cursor-pointer',
        isLocked && 'cursor-not-allowed opacity-60'
      )}
    >
      {/* Status icon */}
      <div
        className={cn(
          'flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium',
          isCompleted && 'bg-green-500 text-white',
          isCurrent && 'bg-git-orange text-white',
          isLocked && 'bg-slate-700 text-slate-500',
          !isCompleted && !isCurrent && !isLocked && 'bg-slate-700 text-slate-300'
        )}
      >
        {isCompleted ? (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : isLocked ? (
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
        ) : (
          stepNumber
        )}
      </div>

      {/* Title */}
      <span className="text-sm font-medium truncate">{title}</span>

      {/* Current indicator */}
      {isCurrent && (
        <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-git-orange/20 text-git-orange">
          Текущий
        </span>
      )}
    </button>
  );
}

/**
 * Компонент для группы заблокированных секций
 */
interface LockedSectionGroupProps {
  children: React.ReactNode;
  className?: string;
}

export function LockedSectionGroup({ children, className }: LockedSectionGroupProps) {
  return <div className={cn('space-y-4', className)}>{children}</div>;
}
